// State that persists across rooms for one run. GameScene restarts for every
// room; everything that must survive a room transition lives here.

import { SECTORS_PER_RUN, ULTIMATE_COST } from '../config/constants';
import { MECHS } from '../data/mechs';
import { createInventory } from '../systems/loot/inventory';
import { generateRoute } from '../systems/world/route';
import type { InventoryApi } from './contracts';
import { bus } from './events';
import { Rng, freshSeed, hashSeed } from './rng';
import type { MechDef, MechId, PityState, RouteGraph, RouteNode, RunStats } from './types';

export interface RunOptions {
  mechId: MechId;
  seed?: number;
  /** Challenge option ids enabled in the hangar. */
  challenges?: string[];
}

export class RunState {
  readonly seed: number;
  readonly mechId: MechId;
  readonly challenges: readonly string[];
  readonly rng: { route: Rng; loot: Rng; combat: Rng };
  readonly inventory: InventoryApi;

  sectorIndex = 0;
  route: RouteGraph;
  nodeId: string;

  hp: number;
  shield: number;
  ultCharge = 0;
  scrap = 0;

  stats: RunStats = {
    kills: 0,
    eliteKills: 0,
    bossKills: 0,
    damageDealt: 0,
    damageTaken: 0,
    roomsCleared: 0,
    orbsOpened: 0,
    itemsFound: 0,
    scrapEarned: 0,
    challengesCompleted: 0,
    timeSec: 0,
    sectorReached: 0,
    highestHit: 0,
  };

  pity: PityState = { sinceAdvanced: 0, sinceWeapon: 0, sinceUseful: 0 };

  /** One-off flags (hints shown, scripted events done...). */
  readonly flags = new Set<string>();

  ended = false;
  victory = false;

  constructor(opts: RunOptions) {
    this.seed = (opts.seed ?? freshSeed()) >>> 0;
    this.mechId = opts.mechId;
    this.challenges = opts.challenges ?? [];
    this.rng = {
      route: new Rng(hashSeed(this.seed, 'route')),
      loot: new Rng(hashSeed(this.seed, 'loot')),
      combat: new Rng(hashSeed(this.seed, 'combat')),
    };
    const mech = this.mech;
    this.inventory = createInventory(mech, this.rng.loot.fork('starting-kit'));
    this.route = generateRoute(0, this.rng.route.fork('sector-0'));
    this.nodeId = this.route.startId;
    this.hp = mech.base.maxHp;
    this.shield = mech.base.maxShield;
  }

  get mech(): MechDef {
    return MECHS[this.mechId];
  }

  get node(): RouteNode {
    return this.route.nodes[this.nodeId];
  }

  get isFinalSector(): boolean {
    return this.sectorIndex >= SECTORS_PER_RUN - 1;
  }

  hasChallenge(id: string): boolean {
    return this.challenges.includes(id);
  }

  enterNode(id: string): void {
    if (!this.route.nodes[id]) throw new Error(`Unknown route node ${id}`);
    this.nodeId = id;
    this.route.nodes[id].visited = true;
  }

  /** Nodes reachable from the current node. */
  nextNodes(): RouteNode[] {
    return this.node.next.map((id) => this.route.nodes[id]).filter(Boolean);
  }

  /** Move to the next sector. Returns false if the run is already in its final sector. */
  advanceSector(): boolean {
    if (this.isFinalSector) return false;
    this.sectorIndex++;
    this.stats.sectorReached = Math.max(this.stats.sectorReached, this.sectorIndex);
    this.route = generateRoute(this.sectorIndex, this.rng.route.fork(`sector-${this.sectorIndex}`));
    this.nodeId = this.route.startId;
    return true;
  }

  addScrap(amount: number): void {
    const delta = Math.round(amount);
    if (delta === 0) return;
    this.scrap = Math.max(0, this.scrap + delta);
    if (delta > 0) this.stats.scrapEarned += delta;
    bus.emit('scrap:changed', { total: this.scrap, delta });
  }

  spendScrap(amount: number): boolean {
    if (this.scrap < amount) return false;
    this.addScrap(-amount);
    return true;
  }

  clampUltimate(): void {
    this.ultCharge = Math.max(0, Math.min(ULTIMATE_COST, this.ultCharge));
  }
}

let current: RunState | null = null;

export function startRun(opts: RunOptions): RunState {
  current = new RunState(opts);
  return current;
}

export function getRun(): RunState | null {
  return current;
}

export function requireRun(): RunState {
  if (!current) throw new Error('No active run');
  return current;
}

export function clearRun(): void {
  current = null;
}
